import { FC, useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  hexToRgb,
  IconButton,
  CircularProgress,
  LinearProgress,
  Modal,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';

import CustomButton from '../../../../../common/CustomButton';
import CustomCountryMultiselect from '../../../../../common/CustomCountryMultiselect';
import CustomInput from '../../../../../common/CustomInput';
import CustomReactDatePicker from '../../../../../common/CustomReactDatePicker';
import AddTravelBuddiesModal from '../AddTravelBuddiesModal';
import TravelBuddiesSection from '../TravelBuddiesSection';
import {
  useCreateAITripPlanningSessionMutation,
  useFinalizeAITripPlanningSessionMutation,
  useGenerateAITripPlanningPreviewMutation,
  useGetDestinationCountriesQuery,
  useSubmitAITripPlanningAnswersMutation,
} from '../../../../../store/services/tripsApi';
import {
  AITripPlanningAnswerObject,
  AITripPlanningCreateSessionPayload,
  AITripPlanningQuestion,
  AITripPlanningSessionResponse,
} from '../../../../../types/aiChat.types';
import { DestinationCountry, TravelBuddy } from '../../../../../types/trip.types';
import { formatDateToLocal } from '../../../../../utils/dateUtils';
import { formatCurrencyAmount } from '../../../../../constants/currency';

import styles from './AITripPlanningModal.module.scss';

type AITripPlanningModalProps = {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
};

type WizardStep = 1 | 2 | 3;

type StepOneState = {
  trip_name: string;
  start_date: string;
  end_date: string;
  adults: string;
  children: string;
};

type StepOneErrors = Partial<
  Record<'trip_name' | 'destination_country' | 'start_date' | 'end_date' | 'adults' | 'children' | 'travellers', string>
>;

type RawQuestion = Record<string, unknown>;

const initialStepOneState: StepOneState = {
  trip_name: '',
  start_date: '',
  end_date: '',
  adults: '1',
  children: '0',
};

const DEFAULT_DEPARTURE_POINT = 'Not specified';
const MAX_TRAVELLER_COUNT = 50;
const MAX_TRIP_NAME_LENGTH = 200;

const safeJsonParse = (value: unknown): unknown => {
  if (typeof value !== 'string') return value;

  const trimmed = value.trim();
  if (!trimmed) return null;

  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
};

const asQuestionsArray = (raw: unknown): RawQuestion[] => {
  const parsed = safeJsonParse(raw);

  if (Array.isArray(parsed)) {
    return parsed.filter(item => !!item && typeof item === 'object') as RawQuestion[];
  }

  if (parsed && typeof parsed === 'object') {
    const candidate = parsed as Record<string, unknown>;
    if (Array.isArray(candidate.questions)) {
      return candidate.questions.filter(item => !!item && typeof item === 'object') as RawQuestion[];
    }
    return [candidate];
  }

  return [];
};

const asOptions = (raw: unknown): { value: string; label: string }[] => {
  if (!Array.isArray(raw)) return [];

  return raw
    .map(option => {
      if (typeof option === 'string' || typeof option === 'number') {
        const normalized = String(option);
        return { value: normalized, label: normalized };
      }

      if (option && typeof option === 'object') {
        const typedOption = option as Record<string, unknown>;
        const valueCandidate =
          typedOption.value ?? typedOption.id ?? typedOption.key ?? typedOption.code ?? typedOption.name;
        const labelCandidate = typedOption.label ?? typedOption.title ?? typedOption.name ?? valueCandidate;

        if (valueCandidate == null || labelCandidate == null) return null;

        return {
          value: String(valueCandidate),
          label: String(labelCandidate),
        };
      }

      return null;
    })
    .filter((item): item is { value: string; label: string } => item !== null);
};

const normalizeQuestion = (question: RawQuestion, index: number): AITripPlanningQuestion => {
  const options = asOptions(question.options ?? question.choices);

  const apiQuestionType = String(question.question_type ?? question.type ?? question.input_type ?? question.kind ?? '')
    .trim()
    .toLowerCase();

  let type: AITripPlanningQuestion['type'] = 'text';

  if (
    apiQuestionType === 'text' ||
    apiQuestionType === 'single_choice' ||
    apiQuestionType === 'multi_choice' ||
    apiQuestionType === 'multiple_choice'
  ) {
    if (apiQuestionType === 'single_choice') type = 'single_choice';
    else if (apiQuestionType === 'multi_choice' || apiQuestionType === 'multiple_choice') type = 'multi_choice';
    else type = 'text';
  } else if (apiQuestionType) {
    const legacy = apiQuestionType;
    const allowMultiple = Boolean(question.allow_multiple ?? question.multiple ?? question.multi_select);
    if (allowMultiple || legacy.includes('multi') || legacy.includes('checkbox')) {
      type = 'multi_choice';
    } else if (
      legacy.includes('single') ||
      legacy.includes('choice') ||
      legacy.includes('radio') ||
      legacy.includes('select') ||
      (!allowMultiple && options.length > 0)
    ) {
      type = 'single_choice';
    } else {
      type = 'text';
    }
  } else {
    const allowMultiple = Boolean(question.allow_multiple ?? question.multiple ?? question.multi_select);
    if (allowMultiple) type = 'multi_choice';
    else if (options.length > 0) type = 'single_choice';
    else type = 'text';
  }

  const idCandidate = question.id ?? question.question_id ?? question.key ?? question.slug ?? `question_${index + 1}`;

  const promptFromApi = question.prompt;
  const promptCandidate =
    promptFromApi != null && String(promptFromApi).trim() !== ''
      ? String(promptFromApi).trim()
      : ([question.title, question.question, question.label, question.text]
          .find(v => v != null && String(v).trim() !== '')
          ?.toString()
          .trim() ?? `Question ${index + 1}`);

  return {
    id: String(idCandidate),
    prompt: promptCandidate,
    type,
    required: Boolean(question.required),
    options: options.length > 0 ? options : undefined,
    raw: question,
  };
};

const normalizePreviewPayload = (rawPreview: unknown): unknown => {
  const parsed = safeJsonParse(rawPreview);
  return parsed ?? rawPreview;
};

type EstimatedBudgetBreakdownItem = {
  category: string;
  amount: number;
};

type EstimatedBudget = {
  currency: string;
  totalAmount: number;
  perPersonAmount?: number;
  confidence?: string;
  breakdown: EstimatedBudgetBreakdownItem[];
  notes?: string;
};

function parseEstimatedBudget(previewPayload: unknown): EstimatedBudget | null {
  if (!previewPayload || typeof previewPayload !== 'object' || Array.isArray(previewPayload)) {
    return null;
  }

  const root = previewPayload as Record<string, unknown>;
  const rawBudget = root.estimated_budget;
  if (rawBudget == null || typeof rawBudget !== 'object' || Array.isArray(rawBudget)) {
    return null;
  }

  const budget = rawBudget as Record<string, unknown>;
  const totalAmount = Number(budget.total_amount);
  if (!Number.isFinite(totalAmount)) return null;

  const currency = typeof budget.currency === 'string' && budget.currency.trim() ? budget.currency.trim() : 'USD';
  const perPersonRaw = Number(budget.per_person_amount);
  const breakdownRaw = Array.isArray(budget.breakdown) ? budget.breakdown : [];
  const breakdown = breakdownRaw
    .map(item => {
      if (!item || typeof item !== 'object') return null;
      const row = item as Record<string, unknown>;
      const amount = Number(row.amount);
      const category = typeof row.category === 'string' ? row.category.trim() : '';
      if (!category || !Number.isFinite(amount)) return null;
      return { category, amount };
    })
    .filter((item): item is EstimatedBudgetBreakdownItem => item != null);

  return {
    currency,
    totalAmount,
    perPersonAmount: Number.isFinite(perPersonRaw) ? perPersonRaw : undefined,
    confidence: typeof budget.confidence === 'string' ? budget.confidence : undefined,
    breakdown,
    notes: typeof budget.notes === 'string' && budget.notes.trim() ? budget.notes.trim() : undefined,
  };
}

type PreviewActivity = {
  id: string;
  title: string;
  description?: string;
  locationLabel: string;
  addressLine?: string;
  placeName?: string;
  placeAddress?: string;
  startLocalIso?: string;
  endLocalIso?: string;
  legacyScheduleLine?: string;
};

type PreviewDayBlock = {
  key: string;
  dateIso: string;
  dayHeading: string;
  activities: PreviewActivity[];
};

type StepTwoSnapshot = {
  questions: AITripPlanningQuestion[];
  answers: Record<string, string | string[]>;
};

function parseDateIsoLocal(dateIso: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateIso.trim());
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]) - 1;
  const da = Number(m[3]);
  const d = new Date(y, mo, da);
  return Number.isNaN(d.getTime()) ? null : d;
}

function getMinEndDateIso(startDateIso: string): string | null {
  const startDate = parseDateIsoLocal(startDateIso);
  if (!startDate) return null;

  const nextDay = new Date(startDate);
  nextDay.setDate(nextDay.getDate() + 1);
  return formatDateToLocal(nextDay);
}

const PREVIEW_LOCALE = 'en-US';

function formatPreviewDayHeading(dateIso: string, fallbackIndex: number): string {
  const d = parseDateIsoLocal(dateIso);
  if (!d) return dateIso.trim() || `Day ${fallbackIndex + 1}`;
  return d.toLocaleDateString(PREVIEW_LOCALE, { weekday: 'long', month: 'short', day: 'numeric' });
}

function formatActivityDateEn(startIso: string): string {
  if (!startIso) return '';
  const s = new Date(startIso);
  if (Number.isNaN(s.getTime())) return '';
  return s.toLocaleDateString(PREVIEW_LOCALE, { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatActivityTimeRangeEn(startIso: string, endIso: string): string {
  const s = startIso ? new Date(startIso) : null;
  const e = endIso ? new Date(endIso) : null;
  const opts: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit', hour12: true };
  if (!s || Number.isNaN(s.getTime())) return '';
  const startStr = s.toLocaleTimeString(PREVIEW_LOCALE, opts);
  if (e && !Number.isNaN(e.getTime())) {
    return `${startStr} - ${e.toLocaleTimeString(PREVIEW_LOCALE, opts)}`;
  }
  return startStr;
}

function parsePreviewItinerary(previewPayload: unknown): PreviewDayBlock[] {
  if (previewPayload == null) return [];

  let daysArray: unknown[] = [];

  if (Array.isArray(previewPayload)) {
    daysArray = previewPayload;
  } else if (typeof previewPayload === 'object') {
    const root = previewPayload as Record<string, unknown>;
    if (Array.isArray(root.days)) daysArray = root.days;
    else if (Array.isArray(root.itinerary)) daysArray = root.itinerary;
    else if (Array.isArray(root.schedule)) daysArray = root.schedule;
    else if (Array.isArray(root.preview)) daysArray = root.preview;
    else if (root.date_iso != null && (Array.isArray(root.activities) || Array.isArray(root.items))) {
      daysArray = [root];
    }
  }

  const fromNewShape: PreviewDayBlock[] = [];

  daysArray.forEach((day, dayIndex) => {
    if (!day || typeof day !== 'object') return;
    const d = day as Record<string, unknown>;
    const dateIso = String(d.date_iso ?? d.date ?? '').trim();
    const activitiesRaw = (d.activities ?? d.items ?? d.stops) as unknown;
    if (!Array.isArray(activitiesRaw)) return;

    const dayHeading = formatPreviewDayHeading(dateIso, dayIndex);

    const activities: PreviewActivity[] = activitiesRaw
      .map((act, i) => {
        if (!act || typeof act !== 'object') return null;
        const a = act as Record<string, unknown>;
        const title = String(a.title ?? a.name ?? 'Activity').trim() || 'Activity';
        const locationLabel = String(a.location_label ?? a.location ?? '').trim();

        const descRaw = a.description ?? a.notes ?? a.summary;
        const description = descRaw != null && String(descRaw).trim() !== '' ? String(descRaw).trim() : undefined;

        const isAddressable = a.addressable === true;
        const placeNameRaw = a.place_name;
        const placeAddressRaw = a.place_address;
        const placeName =
          isAddressable && placeNameRaw != null && String(placeNameRaw).trim() !== ''
            ? String(placeNameRaw).trim()
            : undefined;
        const placeAddress =
          isAddressable && placeAddressRaw != null && String(placeAddressRaw).trim() !== ''
            ? String(placeAddressRaw).trim()
            : undefined;

        const legacyAddrRaw = a.address ?? a.formatted_address ?? a.street_address;
        const addressLine =
          !placeAddress && legacyAddrRaw != null && String(legacyAddrRaw).trim() !== ''
            ? String(legacyAddrRaw).trim()
            : undefined;

        const startLocalIso =
          a.start_local_iso != null ? String(a.start_local_iso) : a.start_iso != null ? String(a.start_iso) : '';
        const endLocalIso =
          a.end_local_iso != null ? String(a.end_local_iso) : a.end_iso != null ? String(a.end_iso) : '';

        const hasStartIso = Boolean(startLocalIso) && !Number.isNaN(new Date(startLocalIso).getTime());
        const legacyTime = String(a.time ?? a.time_range ?? '');
        const legacyScheduleLine = !hasStartIso && legacyTime.trim() !== '' ? legacyTime.trim() : undefined;

        return {
          id: `${dayIndex}-${i}`,
          title,
          ...(description ? { description } : {}),
          locationLabel,
          ...(placeName ? { placeName } : {}),
          ...(placeAddress ? { placeAddress } : {}),
          ...(addressLine ? { addressLine } : {}),
          ...(startLocalIso ? { startLocalIso } : {}),
          ...(endLocalIso ? { endLocalIso } : {}),
          ...(legacyScheduleLine ? { legacyScheduleLine } : {}),
        } as PreviewActivity;
      })
      .filter((item): item is PreviewActivity => item !== null);

    fromNewShape.push({
      key: dateIso || `day-${dayIndex}`,
      dateIso,
      dayHeading,
      activities,
    });
  });

  if (fromNewShape.some(b => b.activities.length > 0)) {
    return fromNewShape.filter(b => b.activities.length > 0);
  }

  return parseLegacyPreviewItinerary(previewPayload);
}

function parseLegacyPreviewItinerary(previewPayload: unknown): PreviewDayBlock[] {
  let daysCandidate: unknown[] | undefined;

  if (Array.isArray(previewPayload)) {
    daysCandidate = previewPayload;
  } else if (previewPayload && typeof previewPayload === 'object') {
    const parsed = previewPayload as Record<string, unknown>;
    const nested = parsed.days ?? parsed.itinerary ?? parsed.preview ?? parsed.schedule;
    daysCandidate = Array.isArray(nested) ? nested : undefined;
  }

  if (!daysCandidate?.length) return [];

  return daysCandidate
    .map((day, dayIndex) => {
      if (!day || typeof day !== 'object') return null;
      const dayObj = day as Record<string, unknown>;
      const dateIso = String(dayObj.date_iso ?? dayObj.date ?? '').trim();
      const dayHeading =
        (dateIso ? formatPreviewDayHeading(dateIso, dayIndex) : null) ??
        String(dayObj.day_label ?? dayObj.title ?? `Day ${dayIndex + 1}`);
      const itemsSource = dayObj.items ?? dayObj.activities ?? dayObj.stops;
      if (!Array.isArray(itemsSource)) {
        return {
          key: dateIso || `legacy-day-${dayIndex}`,
          dateIso,
          dayHeading,
          activities: [] as PreviewActivity[],
        };
      }

      const activities: PreviewActivity[] = itemsSource
        .map((item, itemIndex) => {
          if (!item || typeof item !== 'object') return null;
          const itemObj = item as Record<string, unknown>;
          const title = String(itemObj.title ?? itemObj.name ?? 'Activity Name');
          const descriptionRaw = itemObj.description ?? itemObj.notes;
          const description =
            descriptionRaw != null && String(descriptionRaw).trim() !== '' ? String(descriptionRaw).trim() : undefined;
          const locationLabel = String(itemObj.location_label ?? itemObj.location ?? '').trim();
          const addressRaw = itemObj.address ?? itemObj.formatted_address;
          const addressLine =
            addressRaw != null && String(addressRaw).trim() !== '' ? String(addressRaw).trim() : undefined;
          const legacyScheduleLine = String(itemObj.time ?? itemObj.time_range ?? itemObj.start_time ?? '').trim();

          return {
            id: `${dayIndex}-${itemIndex}`,
            title,
            ...(description ? { description } : {}),
            locationLabel,
            ...(addressLine ? { addressLine } : {}),
            ...(legacyScheduleLine ? { legacyScheduleLine } : {}),
          } as PreviewActivity;
        })
        .filter((item): item is PreviewActivity => item !== null);

      return {
        key: dateIso || `legacy-day-${dayIndex}`,
        dateIso,
        dayHeading,
        activities,
      };
    })
    .filter((day): day is PreviewDayBlock => day !== null);
}

const normalizeSessionStatus = (status: string): string => String(status).trim().toLowerCase();

const isClarifyingStatus = (status: string): boolean => normalizeSessionStatus(status) === 'clarifying';

const isAwaitingPreviewStatus = (status: string): boolean => normalizeSessionStatus(status) === 'awaiting_preview';

const isPreviewReadyStatus = (status: string): boolean => normalizeSessionStatus(status) === 'preview_ready';

function getRtkQueryErrorMessage(error: unknown, fallback: string): string {
  if (!error || typeof error !== 'object') return fallback;
  const err = error as { data?: unknown; message?: string; status?: string; error?: string };
  if (err.status === 'FETCH_ERROR' && typeof err.error === 'string' && err.error.trim()) {
    return err.error.trim();
  }
  const data = err.data;
  if (typeof data === 'string' && data.trim()) return data.trim();
  if (data && typeof data === 'object') {
    const d = data as Record<string, unknown>;
    if (typeof d.message === 'string') return d.message;
    if (typeof d.detail === 'string') return d.detail;
    if (Array.isArray(d.detail) && d.detail.length > 0) {
      const first = d.detail[0];
      if (typeof first === 'string') return first;
    }
    if (Array.isArray(d.non_field_errors) && typeof d.non_field_errors[0] === 'string') {
      return d.non_field_errors[0];
    }
    if (Array.isArray(d.errors) && d.errors.length > 0) {
      const firstItem = d.errors[0];
      if (firstItem && typeof firstItem === 'object') {
        const nested = firstItem as Record<string, unknown>;
        if (typeof nested.detail === 'string') return nested.detail;
      }
    }
  }
  if (typeof err.message === 'string' && err.message && err.message !== 'Rejected') return err.message;
  return fallback;
}

function normalizeAnswerForCompare(value: string | string[] | undefined): string {
  if (value === undefined) return '';
  if (Array.isArray(value)) return [...value].sort().join('\u0001');
  return String(value).trim();
}

function answersChangedSinceSnapshot(
  current: Record<string, string | string[]>,
  snapshot: StepTwoSnapshot | null,
  questions: AITripPlanningQuestion[]
): boolean {
  if (!snapshot?.answers) return true;
  const idList = [...questions.map(q => q.id), ...Object.keys(snapshot.answers)];
  const seen: Record<string, true> = {};
  for (const id of idList) {
    if (seen[id]) continue;
    seen[id] = true;
    const cur = normalizeAnswerForCompare(current[id]);
    const prev = normalizeAnswerForCompare(snapshot.answers[id]);
    if (cur !== prev) return true;
  }
  return false;
}

const AITripPlanningModal: FC<AITripPlanningModalProps> = ({ open, onClose, onSuccess }) => {
  const [step, setStep] = useState<WizardStep>(1);
  const [stepOne, setStepOne] = useState<StepOneState>(initialStepOneState);
  const [selectedCountries, setSelectedCountries] = useState<DestinationCountry[]>([]);
  const [selectedBuddies, setSelectedBuddies] = useState<TravelBuddy[]>([]);
  const [showAddBuddiesModal, setShowAddBuddiesModal] = useState(false);
  const [session, setSession] = useState<AITripPlanningSessionResponse | null>(null);
  const [answerValues, setAnswerValues] = useState<Record<string, string | string[]>>({});
  const [stepTwoSnapshot, setStepTwoSnapshot] = useState<StepTwoSnapshot | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [stepOneErrors, setStepOneErrors] = useState<StepOneErrors>({});
  const [stepTwoErrors, setStepTwoErrors] = useState<Record<string, string>>({});
  const [collapsedPreviewDays, setCollapsedPreviewDays] = useState<Set<string>>(new Set());

  const { data: countries = [] } = useGetDestinationCountriesQuery({});

  const [createSession, { isLoading: isCreatingSession }] = useCreateAITripPlanningSessionMutation();
  const [submitAnswers, { isLoading: isSubmittingAnswers }] = useSubmitAITripPlanningAnswersMutation();
  const [generatePreview, { isLoading: isGeneratingPreview }] = useGenerateAITripPlanningPreviewMutation();
  const [finalizeSession, { isLoading: isFinalizingSession }] = useFinalizeAITripPlanningSessionMutation();

  const pendingQuestions = useMemo(() => {
    if (!session) return [];
    return asQuestionsArray(session.pending_questions).map(normalizeQuestion);
  }, [session]);

  const clarifyingQuestionsForUi = useMemo(() => {
    if (pendingQuestions.length > 0) return pendingQuestions;
    if (step === 2 && stepTwoSnapshot?.questions?.length) return stepTwoSnapshot.questions;
    return [];
  }, [pendingQuestions, step, stepTwoSnapshot]);

  const previewPayload = useMemo(() => {
    if (!session) return null;
    return normalizePreviewPayload(session.preview_payload);
  }, [session]);

  const previewDays = useMemo(() => parsePreviewItinerary(previewPayload), [previewPayload]);
  const estimatedBudget = useMemo(() => parseEstimatedBudget(previewPayload), [previewPayload]);
  const progressValue = useMemo(() => (step / 3) * 100, [step]);

  const isBusy = isCreatingSession || isSubmittingAnswers || isGeneratingPreview || isFinalizingSession;

  const loadingMessage = useMemo(() => {
    if (isFinalizingSession) return 'Creating your trip...';
    if (isGeneratingPreview) return 'Generating trip preview...';
    if (isSubmittingAnswers) return 'Processing your answers...';
    if (isCreatingSession) return 'Starting AI planning...';
    return '';
  }, [isCreatingSession, isSubmittingAnswers, isGeneratingPreview, isFinalizingSession]);
  const hasMissingRequiredStepOneFields =
    !stepOne.trip_name.trim() ||
    selectedCountries.length === 0 ||
    !stepOne.start_date ||
    !stepOne.end_date ||
    stepOne.adults.trim() === '' ||
    stepOne.children.trim() === '';

  useEffect(() => {
    if (!open) return;

    setStep(1);
    setStepOne(initialStepOneState);
    setSelectedCountries([]);
    setSelectedBuddies([]);
    setShowAddBuddiesModal(false);
    setSession(null);
    setAnswerValues({});
    setStepTwoSnapshot(null);
    setErrorMessage(null);
    setStepOneErrors({});
    setStepTwoErrors({});
    setCollapsedPreviewDays(new Set());
  }, [open]);

  const togglePreviewDay = (dayKey: string) => {
    setCollapsedPreviewDays(prev => {
      const next = new Set(prev);
      if (next.has(dayKey)) next.delete(dayKey);
      else next.add(dayKey);
      return next;
    });
  };

  useEffect(() => {
    if (!clarifyingQuestionsForUi.length) return;

    setAnswerValues(prev => {
      const next: Record<string, string | string[]> = { ...prev };
      clarifyingQuestionsForUi.forEach(question => {
        if (next[question.id] !== undefined) return;
        next[question.id] = question.type === 'multi_choice' ? [] : '';
      });
      return next;
    });
  }, [clarifyingQuestionsForUi]);

  const handleClose = () => {
    if (isBusy) return;
    onClose();
  };

  const setStepOneValue = (field: keyof StepOneState, value: string) => {
    setStepOne(prev => ({ ...prev, [field]: value }));
    setStepOneErrors(prev => {
      if (!prev[field] && !prev.travellers) return prev;
      return {
        ...prev,
        [field]: undefined,
        ...(field === 'adults' || field === 'children' ? { travellers: undefined } : {}),
      };
    });
  };

  const handleStartDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newStartDate = event.target.value;

    setStepOne(prev => {
      const shouldClearEndDate = Boolean(prev.end_date && newStartDate && prev.end_date <= newStartDate);

      return {
        ...prev,
        start_date: newStartDate,
        ...(shouldClearEndDate ? { end_date: '' } : {}),
      };
    });
    setStepOneErrors(prev => ({
      ...prev,
      start_date: undefined,
      end_date: undefined,
    }));
  };

  const handleTravellerCountChange = (field: 'adults' | 'children', raw: string) => {
    const digitsOnly = raw.replace(/\D/g, '');
    const value =
      digitsOnly === '' ? '' : String(Math.min(Number(digitsOnly), MAX_TRAVELLER_COUNT));
    setStepOne(prev => ({ ...prev, [field]: value }));
    setStepOneErrors(prev => {
      if (!prev[field] && !prev.travellers) return prev;
      return { ...prev, [field]: undefined, travellers: undefined };
    });
  };

  const validateStepOne = (): StepOneErrors => {
    const nextErrors: StepOneErrors = {};

    if (!stepOne.trip_name.trim()) nextErrors.trip_name = 'Trip name is required.';
    if (selectedCountries.length === 0) nextErrors.destination_country = 'Destination country is required.';
    if (!stepOne.start_date) nextErrors.start_date = 'Start date is required.';
    if (!stepOne.end_date) nextErrors.end_date = 'End date is required.';
    if (stepOne.start_date && stepOne.end_date && stepOne.end_date <= stepOne.start_date) {
      nextErrors.end_date = 'End date must be at least one day after start date.';
    }

    const adultsTrim = stepOne.adults.trim();
    const childrenTrim = stepOne.children.trim();
    if (adultsTrim === '') nextErrors.adults = 'Please enter the number of adults.';
    if (childrenTrim === '') nextErrors.children = 'Please enter the number of children (use 0 if none).';

    const adults = Number(adultsTrim);
    const children = Number(childrenTrim);
    if (adultsTrim !== '' && (!Number.isInteger(adults) || adults < 0)) {
      nextErrors.adults = 'Adults must be a whole number of 0 or more.';
    } else if (adultsTrim !== '' && adults > MAX_TRAVELLER_COUNT) {
      nextErrors.adults = `Maximum ${MAX_TRAVELLER_COUNT} adults.`;
    }
    if (childrenTrim !== '' && (!Number.isInteger(children) || children < 0)) {
      nextErrors.children = 'Children must be a whole number of 0 or more.';
    } else if (childrenTrim !== '' && children > MAX_TRAVELLER_COUNT) {
      nextErrors.children = `Maximum ${MAX_TRAVELLER_COUNT} children.`;
    }
    if (
      !nextErrors.adults &&
      !nextErrors.children &&
      adultsTrim !== '' &&
      childrenTrim !== '' &&
      adults + children < 1
    ) {
      nextErrors.travellers = 'Select at least one traveller (adults or children).';
    }

    return nextErrors;
  };

  const validateStepTwo = (): Record<string, string> => {
    const errors: Record<string, string> = {};

    for (const question of clarifyingQuestionsForUi) {
      if (!question.required) continue;

      const value = answerValues[question.id];
      if (question.type === 'multi_choice') {
        if (!Array.isArray(value) || value.length === 0) {
          errors[question.id] = 'This question is required.';
        }
        continue;
      }

      if (typeof value !== 'string' || !value.trim()) {
        errors[question.id] = 'This question is required.';
      }
    }
    return errors;
  };

  const proceedToPreview = async (sessionId: number, clarifyingSnapshot?: StepTwoSnapshot) => {
    if (clarifyingSnapshot) {
      setStepTwoSnapshot(clarifyingSnapshot);
    }
    const previewSession = await generatePreview({ sessionId }).unwrap();
    setSession(previewSession);
    setStep(3);
  };

  const buildAnswersPayload = (): AITripPlanningAnswerObject => {
    const normalizedAnswers: AITripPlanningAnswerObject = {};
    clarifyingQuestionsForUi.forEach(question => {
      const value = answerValues[question.id];
      if (value === undefined) return;
      if (Array.isArray(value)) {
        normalizedAnswers[question.id] = value;
        return;
      }
      normalizedAnswers[question.id] = typeof value === 'string' ? value.trim() : value;
    });
    return normalizedAnswers;
  };

  const followUpAfterSubmitAnswers = async (
    updatedSession: AITripPlanningSessionResponse,
    clarifyingSnapshot: StepTwoSnapshot
  ) => {
    setSession(updatedSession);

    if (isClarifyingStatus(updatedSession.status)) {
      return;
    }

    if (isPreviewReadyStatus(updatedSession.status)) {
      setStepTwoSnapshot(clarifyingSnapshot);
      setStep(3);
      return;
    }

    if (isAwaitingPreviewStatus(updatedSession.status)) {
      await proceedToPreview(updatedSession.id, clarifyingSnapshot);
      return;
    }

    setErrorMessage(`Unexpected session status after answers: ${updatedSession.status}`);
  };

  const handleCreateSession = async () => {
    const validationErrors = validateStepOne();
    if (Object.keys(validationErrors).length > 0) {
      setStepOneErrors(validationErrors);
      return;
    }

    try {
      setErrorMessage(null);
      setStepOneErrors({});

      const payload: AITripPlanningCreateSessionPayload = {
        structured: {
          name: stepOne.trip_name.trim(),
          departure_point: DEFAULT_DEPARTURE_POINT,
          destination_country: selectedCountries.map(country => country.name).join(', '),
          start_date: stepOne.start_date,
          end_date: stepOne.end_date,
          adults: Number(stepOne.adults),
          children: Number(stepOne.children),
          buddy_ids: selectedBuddies.map(buddy => buddy.id),
        },
      };

      const createdSession = await createSession(payload).unwrap();
      setSession(createdSession);

      if (isClarifyingStatus(createdSession.status)) {
        setStep(2);
        return;
      }

      if (isAwaitingPreviewStatus(createdSession.status)) {
        await proceedToPreview(createdSession.id, undefined);
        return;
      }

      setErrorMessage(`Unexpected session status after create: ${createdSession.status}`);
    } catch (error: any) {
      setErrorMessage(error?.data?.message || 'Failed to start AI planning session.');
    }
  };

  const handleSubmitAnswersAndGeneratePreview = async () => {
    if (!session) return;

    if (!isClarifyingStatus(session.status)) {
      if (isAwaitingPreviewStatus(session.status)) {
        try {
          setErrorMessage(null);
          const snap =
            stepTwoSnapshot ??
            (clarifyingQuestionsForUi.length > 0
              ? { questions: clarifyingQuestionsForUi, answers: { ...answerValues } }
              : undefined);
          await proceedToPreview(session.id, snap);
        } catch (error: any) {
          setErrorMessage(error?.data?.message || 'Failed to generate trip preview.');
        }
        return;
      }
      if (isPreviewReadyStatus(session.status)) {
        const answersDirty = answersChangedSinceSnapshot(answerValues, stepTwoSnapshot, clarifyingQuestionsForUi);
        if (!answersDirty) {
          setErrorMessage(null);
          setStepTwoErrors({});
          setStep(3);
          return;
        }
        if (clarifyingQuestionsForUi.length > 0) {
          const validationErrors = validateStepTwo();
          if (Object.keys(validationErrors).length > 0) {
            setStepTwoErrors(validationErrors);
            return;
          }
        }
        try {
          setErrorMessage(null);
          setStepTwoErrors({});
          const clarifyingSnapshot: StepTwoSnapshot = {
            questions: pendingQuestions.length > 0 ? pendingQuestions : clarifyingQuestionsForUi,
            answers: { ...answerValues },
          };
          const normalizedAnswers = buildAnswersPayload();
          const answersPayload =
            Object.keys(normalizedAnswers).length > 0 ? normalizedAnswers : ({} as AITripPlanningAnswerObject);

          const updatedSession = await submitAnswers({
            sessionId: session.id,
            payload: {
              answers: [answersPayload],
            },
          }).unwrap();

          await followUpAfterSubmitAnswers(updatedSession, clarifyingSnapshot);
        } catch (error: unknown) {
          setErrorMessage(getRtkQueryErrorMessage(error, 'Could not regenerate preview with your updated answers.'));
        }
        return;
      }
      setErrorMessage(`Cannot submit answers for status: ${session.status}`);
      return;
    }

    if (clarifyingQuestionsForUi.length > 0) {
      const validationErrors = validateStepTwo();
      if (Object.keys(validationErrors).length > 0) {
        setStepTwoErrors(validationErrors);
        return;
      }
    }

    try {
      setErrorMessage(null);
      setStepTwoErrors({});

      const clarifyingSnapshot: StepTwoSnapshot = {
        questions: pendingQuestions.length > 0 ? pendingQuestions : clarifyingQuestionsForUi,
        answers: { ...answerValues },
      };

      const normalizedAnswers = buildAnswersPayload();
      const answersPayload =
        Object.keys(normalizedAnswers).length > 0 ? normalizedAnswers : ({} as AITripPlanningAnswerObject);

      const updatedSession = await submitAnswers({
        sessionId: session.id,
        payload: {
          answers: [answersPayload],
        },
      }).unwrap();

      await followUpAfterSubmitAnswers(updatedSession, clarifyingSnapshot);
    } catch (error: unknown) {
      setErrorMessage(getRtkQueryErrorMessage(error, 'Failed to submit AI answers.'));
    }
  };

  const handleFinalize = async () => {
    if (!session) return;

    try {
      setErrorMessage(null);
      await finalizeSession({
        sessionId: session.id,
        payload: {
          save_as: 'sample_trip',
        },
      }).unwrap();
      onSuccess?.();
      onClose();
    } catch (error: any) {
      setErrorMessage(error?.data?.message || 'Failed to finalize and create trip.');
    }
  };

  const handleRemoveBuddy = (buddyId: number) => {
    setSelectedBuddies(prev => prev.filter(buddy => buddy.id !== buddyId));
  };

  const renderStepOne = () => (
    <Box className={styles.stepContent}>
      <Accordion
        defaultExpanded
        className={styles.accordion}
        sx={{ marginBottom: '0 !important' }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          className={styles.accordionSummary}
        >
          <Typography
            variant="h9Bold"
            sx={{ color: '#222B3A', marginBottom: '24px' }}
          >
            Trip Information
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ padding: 0 }}>
          <Box className={styles.formSection}>
            <CustomInput
              inputLabel="Trip Name"
              inputName="trip_name"
              value={stepOne.trip_name}
              error={stepOneErrors.trip_name || ''}
              onChange={event =>
                setStepOneValue('trip_name', event.target.value.slice(0, MAX_TRIP_NAME_LENGTH))
              }
            />

            <Box className={styles.countryFieldBlock}>
              <CustomCountryMultiselect
                options={countries}
                value={selectedCountries}
                onChange={nextValue => {
                  setSelectedCountries(nextValue);
                  setStepOneErrors(prev => ({ ...prev, destination_country: undefined }));
                }}
                inputLabel="Destination Country"
                placeholder="Enter country name"
                error={Boolean(stepOneErrors.destination_country)}
              />
              {stepOneErrors.destination_country && (
                <Typography
                  variant="h11"
                  className={`${styles.fieldError} ${styles.fieldErrorNoGap}`}
                >
                  {stepOneErrors.destination_country}
                </Typography>
              )}
            </Box>

            <Box className={styles.dateRow}>
              <CustomReactDatePicker
                name="start_date"
                inputLabel="Start Date"
                placeholder="Select start date"
                value={stepOne.start_date}
                error={Boolean(stepOneErrors.start_date)}
                helperText={stepOneErrors.start_date}
                onChange={handleStartDateChange}
                min={new Date().toISOString().split('T')[0]}
              />
              <CustomReactDatePicker
                name="end_date"
                inputLabel="End Date"
                placeholder="Select end date"
                value={stepOne.end_date}
                error={Boolean(stepOneErrors.end_date)}
                helperText={stepOneErrors.end_date}
                onChange={event => setStepOneValue('end_date', event.target.value)}
                min={getMinEndDateIso(stepOne.start_date) || new Date().toISOString().split('T')[0]}
              />
            </Box>

            <Box className={styles.travellersBlock}>
              <Typography
                variant="h11Semibold"
                sx={{ color: '#222B3A' }}
              >
                Travellers
              </Typography>
              <Box className={styles.travellersRow}>
                <TextField
                  label="Adults"
                  type="text"
                  inputMode="numeric"
                  variant="outlined"
                  value={stepOne.adults}
                  onChange={event => handleTravellerCountChange('adults', event.target.value)}
                  className={styles.nativeInput}
                  error={Boolean(stepOneErrors.adults)}
                  helperText={stepOneErrors.adults}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', max: MAX_TRAVELLER_COUNT }}
                />
                <TextField
                  label="Children"
                  type="text"
                  inputMode="numeric"
                  variant="outlined"
                  value={stepOne.children}
                  onChange={event => handleTravellerCountChange('children', event.target.value)}
                  className={styles.nativeInput}
                  error={Boolean(stepOneErrors.children)}
                  helperText={stepOneErrors.children}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', max: MAX_TRAVELLER_COUNT }}
                />
              </Box>
              {stepOneErrors.travellers && (
                <Typography
                  variant="h11"
                  className={styles.fieldError}
                >
                  {stepOneErrors.travellers}
                </Typography>
              )}
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>

      <Accordion className={styles.accordion}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          className={styles.accordionSummary}
        >
          <Typography
            variant="h9Bold"
            sx={{ color: '#222B3A' }}
          >
            Travel Buddies
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ padding: 0 }}>
          <TravelBuddiesSection
            selectedBuddies={selectedBuddies}
            onAddBuddies={() => setShowAddBuddiesModal(true)}
            onRemoveBuddy={handleRemoveBuddy}
          />
        </AccordionDetails>
      </Accordion>

      <Box className={styles.footer}>
        <CustomButton
          variant="outlined"
          onClick={handleClose}
          disabled={isBusy}
        >
          Cancel
        </CustomButton>
        <CustomButton
          variant="contained"
          onClick={handleCreateSession}
          disabled={isBusy || hasMissingRequiredStepOneFields}
        >
          {isCreatingSession ? 'Starting...' : 'Continue'}
        </CustomButton>
      </Box>
    </Box>
  );

  const renderQuestionInput = (question: AITripPlanningQuestion, disabled: boolean) => {
    const currentValue = answerValues[question.id];

    if (question.type === 'multi_choice') {
      const selectedValues = Array.isArray(currentValue) ? currentValue : [];

      if (!question.options?.length) {
        return (
          <Typography
            variant="h12"
            sx={{ color: '#5E6A81' }}
          >
            No answer options were provided for this question.
          </Typography>
        );
      }

      return (
        <FormGroup>
          {question.options.map(option => (
            <FormControlLabel
              key={`${question.id}-${option.value}`}
              disabled={disabled}
              sx={{
                '& .MuiFormControlLabel-label': {
                  fontSize: '14px',
                  color: '#262626',
                },
              }}
              control={
                <Checkbox
                  disabled={disabled}
                  checked={selectedValues.includes(option.value)}
                  onChange={() => {
                    const nextValues = selectedValues.includes(option.value)
                      ? selectedValues.filter(item => item !== option.value)
                      : [...selectedValues, option.value];
                    setAnswerValues(prev => ({ ...prev, [question.id]: nextValues }));
                    setStepTwoErrors(prev => ({ ...prev, [question.id]: '' }));
                  }}
                  icon={
                    <Box
                      component="span"
                      sx={{
                        width: 18,
                        height: 18,
                        borderRadius: '4px',
                        border: '1.3px solid #9096A2',
                        boxSizing: 'border-box',
                        display: 'inline-block',
                        backgroundColor: '#FFFFFF',
                      }}
                    />
                  }
                  checkedIcon={
                    <Box
                      component="span"
                      sx={{
                        width: 18,
                        height: 18,
                        borderRadius: '4px',
                        border: '1.3px solid #0098F0',
                        boxSizing: 'border-box',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#0098F0',
                        '&::after': {
                          content: '""',
                          width: 5,
                          height: 9,
                          border: 'solid #FFFFFF',
                          borderWidth: '0 2px 2px 0',
                          transform: 'rotate(45deg)',
                          marginTop: '-1px',
                        },
                      }}
                    />
                  }
                />
              }
              label={option.label}
            />
          ))}
        </FormGroup>
      );
    }

    if (question.type === 'single_choice') {
      if (!question.options?.length) {
        return (
          <Typography
            variant="h12"
            sx={{ color: '#5E6A81' }}
          >
            No answer options were provided for this question.
          </Typography>
        );
      }

      return (
        <FormControl>
          <RadioGroup
            value={typeof currentValue === 'string' ? currentValue : ''}
            onChange={event => {
              setAnswerValues(prev => ({ ...prev, [question.id]: event.target.value }));
              setStepTwoErrors(prev => ({ ...prev, [question.id]: '' }));
            }}
          >
            {question.options.map(option => (
              <FormControlLabel
                key={`${question.id}-${option.value}`}
                value={option.value}
                disabled={disabled}
                sx={{
                  '& .MuiFormControlLabel-label': {
                    fontSize: '14px',
                    color: '#262626',
                  },
                }}
                control={
                  <Radio
                    disabled={disabled}
                    icon={
                      <Box
                        component="span"
                        sx={{
                          width: 18,
                          height: 18,
                          borderRadius: '50%',
                          border: '1.3px solid #9096A2',
                          boxSizing: 'border-box',
                          display: 'inline-block',
                        }}
                      />
                    }
                    checkedIcon={
                      <Box
                        component="span"
                        sx={{
                          width: 18,
                          height: 18,
                          borderRadius: '50%',
                          border: '1.3px solid #0098F0',
                          boxSizing: 'border-box',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          '&::after': {
                            content: '""',
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            backgroundColor: '#0098F0',
                          },
                        }}
                      />
                    }
                  />
                }
                label={option.label}
              />
            ))}
          </RadioGroup>
        </FormControl>
      );
    }

    return (
      <CustomInput
        inputLabel=""
        placeholder="Type your answer"
        disabled={disabled}
        value={typeof currentValue === 'string' ? currentValue : ''}
        onChange={event => {
          setAnswerValues(prev => ({ ...prev, [question.id]: event.target.value }));
          setStepTwoErrors(prev => ({ ...prev, [question.id]: '' }));
        }}
      />
    );
  };

  const renderStepTwo = () => {
    const hasRequiredAnswersMissing = clarifyingQuestionsForUi.length > 0 && Object.keys(validateStepTwo()).length > 0;
    const clarifyingFieldsLocked = isSubmittingAnswers || isGeneratingPreview;

    return (
      <Box className={styles.stepContent}>
        <Box className={styles.stepContentWrapper}>
          <Typography
            variant="h9Bold"
            className={styles.stepTitle}
          >
            Clarifying Questions
          </Typography>

          <Box className={styles.questionsList}>
            {clarifyingQuestionsForUi.map(question => (
              <Box
                key={question.id}
                className={styles.questionCard}
              >
                <Typography
                  variant="h11Bold"
                  className={styles.questionTitle}
                  sx={{ fontWeight: 600, marginBottom: '8px' }}
                >
                  {question.prompt}
                  {question.required ? ' *' : ''}
                </Typography>
                {renderQuestionInput(question, clarifyingFieldsLocked)}
                {stepTwoErrors[question.id] && (
                  <Typography
                    variant="h11"
                    className={styles.fieldError}
                  >
                    {stepTwoErrors[question.id]}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        </Box>

        <Box className={styles.footer}>
          <CustomButton
            variant="outlined"
            onClick={() => setStep(1)}
            disabled={isBusy}
          >
            Back
          </CustomButton>
          <CustomButton
            variant="contained"
            onClick={handleSubmitAnswersAndGeneratePreview}
            disabled={isBusy || hasRequiredAnswersMissing}
          >
            {isSubmittingAnswers || isGeneratingPreview ? 'Generating...' : 'Generate Trip Preview'}
          </CustomButton>
        </Box>
      </Box>
    );
  };

  const renderStepThree = () => (
    <Box className={styles.stepContent}>
      <Typography
        variant="h9Bold"
        className={styles.stepTitle}
      >
        Trip Preview
      </Typography>

      {previewDays.length > 0 ? (
        <Box className={styles.previewDaysList}>
          {previewDays.map(day => {
            const isCollapsed = collapsedPreviewDays.has(day.key);

            return (
              <Box
                key={day.key}
                className={styles.previewDaySection}
              >
                <Box className={styles.previewDayHeader}>
                  <Typography
                    variant="h7Bold"
                    className={styles.previewDayHeadingText}
                  >
                    {day.dayHeading}
                  </Typography>
                  <IconButton
                    size="small"
                    className={styles.previewDayToggleButton}
                    onClick={() => togglePreviewDay(day.key)}
                  >
                    {isCollapsed ? <AddIcon /> : <RemoveIcon />}
                  </IconButton>
                </Box>
                {!isCollapsed && (
                  <Box className={styles.previewItemsList}>
                    {day.activities.map(activity => {
                      const startIso = activity.startLocalIso ?? '';
                      const endIso = activity.endLocalIso ?? '';
                      const dateLine = startIso || endIso ? formatActivityDateEn(startIso || endIso) : '';
                      const timeLine = startIso || endIso ? formatActivityTimeRangeEn(startIso, endIso) : '';
                      const legacyOnly = !dateLine && !timeLine && activity.legacyScheduleLine;

                      return (
                        <Box
                          key={activity.id}
                          className={styles.previewItemCard}
                        >
                          <Typography
                            variant="h9"
                            sx={{ color: '#222B3A', fontWeight: 600 }}
                          >
                            {activity.title}
                          </Typography>
                          {activity.description ? (
                            <Typography
                              variant="h10"
                              sx={{ color: '#5E6A81' }}
                            >
                              {activity.description}
                            </Typography>
                          ) : null}
                          {activity.locationLabel ? (
                            <Typography
                              variant="h11"
                              sx={{ color: '#222B3A' }}
                            >
                              {activity.locationLabel}
                            </Typography>
                          ) : null}
                          {activity.placeName ? (
                            <Typography
                              variant="h11Semibold"
                              sx={{ color: '#222B3A' }}
                            >
                              {activity.placeName}
                            </Typography>
                          ) : null}
                          {activity.placeAddress ? (
                            <Typography
                              variant="h11"
                              sx={{ color: '#222B3A' }}
                            >
                              {activity.placeAddress}
                            </Typography>
                          ) : null}
                          {!activity.placeAddress && activity.addressLine ? (
                            <Typography
                              variant="h11"
                              sx={{ color: '#222B3A' }}
                            >
                              {activity.addressLine}
                            </Typography>
                          ) : null}
                          {dateLine ? (
                            <Box className={styles.previewTimeRow}>
                              <CalendarTodayOutlinedIcon className={styles.previewTimeIcon} />
                              <Typography
                                variant="h11"
                                sx={{ color: '#5E6A81' }}
                              >
                                {dateLine}
                              </Typography>
                              {timeLine ? (
                                <>
                                  <span className={styles.previewTimeSeparator} />
                                  <Typography
                                    variant="h11"
                                    sx={{ color: '#5E6A81' }}
                                  >
                                    {timeLine}
                                  </Typography>
                                </>
                              ) : null}
                            </Box>
                          ) : null}
                          {legacyOnly ? (
                            <Typography
                              variant="h11"
                              sx={{ color: '#222B3A' }}
                            >
                              {activity.legacyScheduleLine}
                            </Typography>
                          ) : null}
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      ) : (
        <Box className={styles.previewCard}>
          <pre className={styles.previewContent}>{JSON.stringify(previewPayload, null, 2)}</pre>
        </Box>
      )}

      {estimatedBudget ? (
        <Box className={styles.budgetCard}>
          <Typography
            variant="h10Semibold"
            className={styles.budgetLabel}
          >
            Estimated trip budget
          </Typography>
          <Typography
            variant="h12"
            className={styles.budgetDisclaimer}
          >
            This is an approximate budget generated by AI. It is not final and will not be added to your trip’s total
            budget.
          </Typography>
          <Typography
            variant="h7Bold"
            className={styles.budgetTotal}
          >
            {formatCurrencyAmount(estimatedBudget.totalAmount, estimatedBudget.currency)}{' '}
            <span className={styles.budgetCurrency}>{estimatedBudget.currency}</span>
          </Typography>
          {estimatedBudget.perPersonAmount != null ? (
            <Typography
              variant="h11"
              className={styles.budgetPerPerson}
            >
              {formatCurrencyAmount(estimatedBudget.perPersonAmount, estimatedBudget.currency)}{' '}
              {estimatedBudget.currency} per person
            </Typography>
          ) : null}
          {estimatedBudget.breakdown.length > 0 ? (
            <Box className={styles.budgetBreakdown}>
              {estimatedBudget.breakdown.map(item => (
                <Box
                  key={item.category}
                  className={styles.budgetBreakdownRow}
                >
                  <Typography
                    variant="h11"
                    className={styles.budgetBreakdownCategory}
                  >
                    {item.category}
                  </Typography>
                  <Typography
                    variant="h11Semibold"
                    className={styles.budgetBreakdownAmount}
                  >
                    {formatCurrencyAmount(item.amount, estimatedBudget.currency)}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : null}
          {estimatedBudget.notes ? (
            <Typography
              variant="h12"
              className={styles.budgetNotes}
            >
              {estimatedBudget.notes}
            </Typography>
          ) : null}
        </Box>
      ) : null}

      <Box className={styles.footer}>
        <CustomButton
          variant="outlined"
          onClick={() => {
            if (stepTwoSnapshot) {
              setAnswerValues({ ...stepTwoSnapshot.answers });
            }
            setStep(2);
          }}
          disabled={isBusy}
        >
          Back
        </CustomButton>
        <CustomButton
          variant="contained"
          onClick={handleFinalize}
          disabled={isBusy}
        >
          {isFinalizingSession ? 'Finalizing...' : 'Finalize Trip'}
        </CustomButton>
      </Box>
    </Box>
  );

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="ai-trip-planning-modal"
        sx={{
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(34, 43, 58, 0.2)',
          },
        }}
      >
        <Box className={styles.modalWrapper}>
          <Box className={styles.modalFixed}>
            <Box className={styles.modalHeader}>
              <Typography
                variant="h5Bold"
                sx={{ width: '100%', textAlign: 'center' }}
              >
                Plan a Trip with AI
              </Typography>
              <Box sx={{ position: 'absolute', top: 16, right: 20, cursor: 'pointer' }}>
                <CloseIcon
                  sx={{ width: '30px', height: '30px', color: '#5E6A81' }}
                  onClick={handleClose}
                />
              </Box>
            </Box>

            <Box className={styles.progressBlock}>
              <LinearProgress
                variant="determinate"
                value={progressValue}
                className={styles.progressBar}
              />
              <Typography
                variant="h11"
                className={styles.progressLabel}
              >
                Step {step} of 3
              </Typography>
            </Box>

            {errorMessage && (
              <Typography
                variant="h11"
                className={styles.errorText}
              >
                {errorMessage}
              </Typography>
            )}
          </Box>

          <Box className={styles.modalContentArea}>
            <Box className={styles.modalScroll}>
              {step === 1 && renderStepOne()}
              {step === 2 && renderStepTwo()}
              {step === 3 && renderStepThree()}
            </Box>

            {isBusy && (
              <Box
                className={styles.loadingOverlay}
                aria-busy="true"
                aria-live="polite"
              >
                <Box className={styles.loadingOverlayContent}>
                  <CircularProgress
                    size={48}
                    sx={{ color: '#0098F0' }}
                  />
                  {loadingMessage ? (
                    <Typography
                      variant="h11"
                      className={styles.loadingOverlayText}
                    >
                      {loadingMessage}
                    </Typography>
                  ) : null}
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Modal>

      <AddTravelBuddiesModal
        open={showAddBuddiesModal}
        onClose={() => setShowAddBuddiesModal(false)}
        selectedBuddies={selectedBuddies}
        onBuddiesChange={setSelectedBuddies}
        showBackButton={false}
      />
    </>
  );
};

export default AITripPlanningModal;
